import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '@/components/ui/card';
import { Edit, Trash2, AlertTriangle, FileText } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Calendar } from 'lucide-react';
import { Info, Users, Laptop, Calendar as LucideCalendar, Tag, ListTodo } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem
} from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useState } from 'react';
import { AddOnboardingTaskDialog } from './AddOnboardingTaskDialog';
import { updateOnboardingTask } from '@/services/onboardingTask.service';
import { toast } from 'sonner';
import { OnboardingTask } from '@/types/models';

interface ViewOnboardingTasksProps {
  tasks: OnboardingTask[];
  isLoading: boolean;
  onRefresh: () => void;
  onEdit?: (task: OnboardingTask) => void;
  onDelete?: (task: OnboardingTask) => void;
  onView?: (task: OnboardingTask) => void; // Add view handler
}

// Professional icon mapping for categories
const categoryIcons: Record<string, React.ReactNode> = {
  'HR & Administration': <Users className="h-5 w-5 text-blue-500" />, // Lucide Users
  'IT & Technical': <Laptop className="h-5 w-5 text-green-500" />, // Lucide Laptop
  'Training & Orientation': <LucideCalendar className="h-5 w-5 text-purple-500" />, // Lucide Calendar
  'Uncategorized': <Info className="h-5 w-5 text-gray-400" />, // Lucide Info
  'Documentation': <FileText className="h-5 w-5 text-orange-500" />, // Lucide Document
};

const getCategoryColor = (category: string | undefined) => {
  if (category === 'HR & Administration') return 'border-l-4 border-blue-400';
  if (category === 'IT & Technical') return 'border-l-4 border-green-400';
  if (category === 'Training & Orientation') return 'border-l-4 border-purple-400';
  if (category === 'Documentation') return 'border-l-4 border-orange-400';
  return 'border-l-4 border-gray-200';
};

export const ViewOnboardingTasks: React.FC<ViewOnboardingTasksProps> = ({ tasks, isLoading, onRefresh, onEdit, onDelete, onView }) => {
  // Group tasks by category
  const groupedTasks: { [category: string]: OnboardingTask[] } = tasks.reduce((acc, task) => {
    const cat = task.category || 'Uncategorized';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(task);
    return acc;
  }, {} as { [category: string]: OnboardingTask[] });

  // Get all unique categories (preserve order)
  const categories = Object.keys(groupedTasks);

  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<OnboardingTask | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTask, setEditTask] = useState<OnboardingTask | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<OnboardingTask | null>(null);

  const handleView = (task: OnboardingTask) => {
    setSelectedTask(task);
    setViewDialogOpen(true);
    if (onView) onView(task);
  };

  const handleEdit = (task: OnboardingTask) => {

    if (onEdit) {
      // Use parent's edit handler if provided
      onEdit(task);
    } else {
      // Fallback: use local edit dialog
      setEditTask(task);
      setEditDialogOpen(true);
    }
  };

  const handleEditSubmit = async (data: any) => {
    if (!editTask) return;
    try {

      // Map the form data to API format
      const apiData = {
        title: data.title,
        description: data.description,
        category: data.category,
        dueDate: data.dueDate,
      };


      const taskId = editTask.id || editTask._id;
      if (!taskId) {
        console.error('Edit task has no ID:', editTask);
        toast.error('Cannot update task: Invalid task ID');
        return;
      }
      await updateOnboardingTask(taskId, apiData);
      setEditDialogOpen(false);
      setEditTask(null);
      onRefresh();
      toast.success('Task updated successfully');
    } catch (error: any) {
      console.error('Error updating task:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Failed to update task. Please try again.');
    }
  };

  const handleDeleteClick = (task: OnboardingTask) => {

    setTaskToDelete(task);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {

    if (taskToDelete) {
      // Validate task ID - check for both id and _id
      const taskId = taskToDelete.id || taskToDelete._id;
      if (!taskId) {
        console.error('Task has no ID:', taskToDelete);
        toast.error('Cannot delete task: Invalid task ID');
        setDeleteDialogOpen(false);
        setTaskToDelete(null);
        return;
      }

      try {
        if (onDelete) {

          // Use parent's delete handler if provided
          await onDelete(taskToDelete);
        } else {
          onRefresh();
        }
        setDeleteDialogOpen(false);
        setTaskToDelete(null);
        setViewDialogOpen(false); // Close the view dialog
        toast.success('Task deleted successfully');
      } catch (error: any) {
        console.error('Error deleting task:', error);
        console.error('Error details:', error.response?.data);

        let errorMessage = 'Failed to delete task. Please try again.';
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response?.status === 404) {
          errorMessage = 'Task not found';
        } else if (error.response?.status === 403) {
          errorMessage = 'You do not have permission to delete this task';
        }

        toast.error(errorMessage);
        // Keep dialog open if there's an error
      }
    } else {

      setDeleteDialogOpen(false);
      setTaskToDelete(null);
    }
  };

  return (
    <div className="space-y-4">
      {isLoading ? (
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-2" />
          <span>Loading tasks...</span>
        </div>
      ) : tasks.length === 0 ? (
        <div className="text-center text-gray-500 py-8">No onboarding tasks found.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {categories.map(category => (
            <Card key={category} className="rounded-xl shadow-sm p-0 flex flex-col bg-white border border-gray-100">
              <CardHeader className="border-b px-4 pt-3 pb-2 bg-white rounded-t-xl">
                <div className="flex items-center gap-2 mb-1">
                  {categoryIcons[category] || categoryIcons['Uncategorized']}
                  <span className="text-lg font-semibold text-gray-900">{category}</span>
                </div>
                <span className="text-xs text-gray-400">{groupedTasks[category].length} task{groupedTasks[category].length > 1 ? 's' : ''}</span>
              </CardHeader>
              <CardContent className="flex flex-col gap-0 py-2 px-3">
                {groupedTasks[category].map((task, idx) => (
                  <React.Fragment key={task.id || task._id}>
                    <Card
                      className={`relative flex flex-col bg-white border border-gray-100 shadow-sm hover:shadow-md transition-shadow rounded-lg p-0 mb-0 ${getCategoryColor(category)}`}
                    >
                      <div className="flex items-start justify-between px-3 pt-3 pb-1">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <CardTitle className="text-sm font-semibold leading-snug mb-1 text-gray-900 break-words">{task.title}</CardTitle>
                            </div>
                            <div className="flex items-center gap-1 ml-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-full px-2 py-0.5 text-xs font-semibold shadow-none border-none h-6"
                                aria-label="View Task"
                                onClick={() => handleView(task)}
                              >
                                View
                              </Button>
                            </div>
                          </div>
                          {task.description && (
                            <CardDescription className="text-xs mt-1 text-gray-500 break-words line-clamp-2">{task.description}</CardDescription>
                          )}
                        </div>
                      </div>

                    </Card>
                    {/* Divider between tasks, except after last */}
                    {idx < groupedTasks[category].length - 1 && (
                      <div className="w-full h-px bg-gray-100 my-2" />
                    )}
                  </React.Fragment>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* View Task Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-[900px] h-[90vh] flex flex-col p-0 rounded-xl overflow-hidden">
          {/* Hidden accessible title/description */}
          <DialogHeader className="sr-only">
            <DialogTitle>Task Details</DialogTitle>
            <DialogDescription>Detailed view of the selected onboarding task</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            <div className="p-6 space-y-6">
              {selectedTask && (
                <>
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-6">
                        <div className="w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center text-white text-xl font-bold shadow-lg">
                          <ListTodo className="h-8 w-8" />
                        </div>
                        <div>
                          <h1 className="text-2xl font-bold text-gray-900 mb-1">
                            {selectedTask.title || 'Untitled Task'}
                          </h1>
                          <p className="text-gray-600 text-base font-medium">
                            {selectedTask.category || 'General'}
                          </p>
                          <div className="flex items-center gap-4 mt-2">
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              Onboarding Task
                            </Badge>

                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm" className="bg-gray-50 hover:bg-gray-100">
                              Actions
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem onClick={() => {
                              handleEdit(selectedTask);
                              setViewDialogOpen(false);
                            }} className="cursor-pointer">
                              <Edit className="h-4 w-4 mr-2" />
                              Edit Task
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                handleDeleteClick(selectedTask);
                                setViewDialogOpen(false);
                              }}
                              className="cursor-pointer text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Task
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-blue-50 rounded-xl p-6 shadow-sm border border-blue-100">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                          <ListTodo className="h-5 w-5 text-blue-600" />
                        </div>
                        <h2 className="text-xl font-bold text-gray-900">Task Details</h2>
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Tag className="h-4 w-4 text-gray-400" />
                          <span className="text-sm">{selectedTask.category || 'General'}</span>
                        </div>

                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-gray-400" />
                          <span className="text-sm">
                            Created by: {typeof selectedTask.createdBy === 'string'
                              ? selectedTask.createdBy
                              : selectedTask.createdBy
                                ? `${selectedTask.createdBy.firstName} ${selectedTask.createdBy.lastName}`
                                : 'Unknown'
                            }
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-green-50 rounded-xl p-6 shadow-sm border border-green-100">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                          <Calendar className="h-5 w-5 text-green-600" />
                        </div>
                        <h2 className="text-xl font-bold text-gray-900">Timeline</h2>
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span className="text-sm">
                            Created: {selectedTask.createdAt ? new Date(selectedTask.createdAt).toLocaleDateString() : 'Unknown'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span className="text-sm">
                            Created Time: {selectedTask.createdAt ? new Date(selectedTask.createdAt).toLocaleTimeString() : 'Unknown'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span className="text-sm">
                            Last Updated: {selectedTask.updatedAt ? new Date(selectedTask.updatedAt).toLocaleDateString() : 'Not updated'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-yellow-50 rounded-xl p-6 shadow-sm border border-yellow-100">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center">
                        <FileText className="h-5 w-5 text-yellow-600" />
                      </div>
                      <h2 className="text-xl font-bold text-gray-900">Task Description</h2>
                    </div>
                    <p className="text-gray-700 text-sm whitespace-pre-wrap">
                      {selectedTask.description || 'No description provided for this task.'}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Task Dialog */}
      <AddOnboardingTaskDialog
        open={editDialogOpen}
        onOpenChange={(open) => {
          setEditDialogOpen(open);
          if (!open) setEditTask(null);
        }}
        onSubmit={handleEditSubmit}
        initialData={editTask || undefined}
        isEdit={true}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Delete Onboarding Task
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{taskToDelete?.title}"?
              <br />
              <span className="text-red-600 font-medium">This action cannot be undone.</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              className="flex-1"
            >
              Delete Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}; 